import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CButton,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from "@coreui/react";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { validateWithdrawAddress } from "@src/utils/withdrawAddressValidators";
import type * as T from "@src/types";

import "./WithdrawAddressFormModal.scss";

interface Props {
  visible: boolean;
  networks: T.TDT.ApiWithdrawNetwork[];
  onClose: () => void;
  onCreated: (address: T.TDT.ApiWithdrawAddress) => void;
}

interface FieldErrors {
  network?: string;
  address?: string;
  label?: string;
}

export const WithdrawAddressFormModal: React.FC<Props> = ({
  visible,
  networks,
  onClose,
  onCreated,
}) => {
  const { showToast } = useToastActions();
  const [networkCode, setNetworkKey] = useState("");
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const selectedNetwork = useMemo(
    () => networks.find((n) => n.code === networkCode),
    [networks, networkCode],
  );

  // Reset state each time the modal opens
  useEffect(() => {
    if (visible) {
      setNetworkKey("");
      setAddress("");
      setLabel("");
      setErrors({});
      setSubmitting(false);
    }
  }, [visible]);

  const clearFieldError = useCallback((field: keyof FieldErrors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const trimmedAddress = address.trim();
      const trimmedLabel = label.trim();
      const newErrors: FieldErrors = {};

      if (!selectedNetwork) {
        newErrors.network = "Select a network";
      }
      if (!trimmedAddress) {
        newErrors.address = "Address is required";
      } else if (selectedNetwork) {
        const validation = validateWithdrawAddress(trimmedAddress, selectedNetwork.address_format);
        if (!validation.valid) {
          newErrors.address = validation.error ?? "Invalid address";
        }
      }
      if (!trimmedLabel) {
        newErrors.label = "Label is required";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        showToast({
          message: Object.values(newErrors).find(Boolean) ?? "Review the highlighted fields",
          type: "warning",
        });
        return;
      }
      setErrors({});

      setSubmitting(true);
      try {
        const created = await tdApi.createWithdrawAddress({
          label: trimmedLabel,
          address: trimmedAddress,
          network_code: selectedNetwork!.code,
        });
        if (!created) {
          showToast({ message: "Failed to create address", type: "danger" });
          return;
        }
        showToast({ message: `Address '${created.label}' saved`, type: "success" });
        onCreated(created);
        onClose();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        showToast({ message: msg, type: "danger", title: "Failed to save address" });
      } finally {
        setSubmitting(false);
      }
    },
    [selectedNetwork, label, address, showToast, onCreated, onClose],
  );

  return (
    <CModal
      className="WithdrawAddressFormModal"
      visible={visible}
      onClose={onClose}
      backdrop="static"
    >
      <CModalHeader closeButton>
        <CModalTitle>Add Withdrawal Address</CModalTitle>
      </CModalHeader>

      <CForm onSubmit={handleSubmit} noValidate>
        <CModalBody>
          <div className="mb-2">
            <CFormLabel htmlFor="withdraw-network">Network</CFormLabel>
            <CFormSelect
              id="withdraw-network"
              value={networkCode}
              invalid={!!errors.network}
              onChange={(e) => {
                setNetworkKey(e.target.value);
                clearFieldError("network");
              }}
              disabled={submitting}
            >
              <option value="">Select a network…</option>
              {networks.map((n) => (
                <option key={n.code} value={n.code}>
                  {n.display_name} ({n.code})
                </option>
              ))}
            </CFormSelect>
            <div className="WithdrawAddressFormModal-fieldError">{errors.network ?? ""}</div>
          </div>

          <div className="mb-2">
            <CFormLabel htmlFor="withdraw-address">Address</CFormLabel>
            <CFormInput
              id="withdraw-address"
              type="text"
              value={address}
              invalid={!!errors.address}
              onChange={(e) => {
                setAddress(e.target.value);
                clearFieldError("address");
              }}
              placeholder="On-chain wallet address"
              disabled={submitting}
              autoComplete="off"
              spellCheck={false}
            />
            <div className="WithdrawAddressFormModal-fieldError">{errors.address ?? ""}</div>
          </div>

          <div className="mb-2">
            <CFormLabel htmlFor="withdraw-label">Label</CFormLabel>
            <CFormInput
              id="withdraw-label"
              type="text"
              value={label}
              invalid={!!errors.label}
              onChange={(e) => {
                setLabel(e.target.value);
                clearFieldError("label");
              }}
              placeholder="e.g. Cold wallet"
              disabled={submitting}
              maxLength={100}
            />
            <div className="WithdrawAddressFormModal-fieldError">{errors.label ?? ""}</div>
          </div>
        </CModalBody>

        <CModalFooter>
          <CButton color="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </CButton>
          <CButton color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <CSpinner size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </CButton>
        </CModalFooter>
      </CForm>
    </CModal>
  );
};
